<!-- Copyright (c) 2021-2022 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details. -->
<template>
  <div class="position-relative">
    <FrInputLayout
      :id="id"
      :name="name"
      :description="description"
      :errors="errors"
      :is-html="isHtml"
      :label="label"
      :validation="validation"
      :validation-immediate="validationImmediate"
      :customMessages="customMessages">
      <input
        v-model="inputValue"
        ref="input"
        type="date"
        :class="[{'is-invalid': errorMessages && errorMessages.length, 'polyfill-placeholder': label }, 'form-control']"
        :data-vv-as="label"
        :disabled="disabled"
        :id="id"
        :name="name"
        :readonly="readonly"
        :min="`${min.getFullYear()}-${min.getMonth() + 1}-${min.getDate()}`"
        :max="`${max.getFullYear()}-${max.getMonth() + 1}-${max.getDate()}`"
        @input="emitDateValue(inputValue)">
    </FrInputLayout>
    <BFormDatepicker
      v-model="inputValue"
      :dropleft="dropleft"
      button-only
      label-help=""
      ref="datePicker"
      :class="[{'is-invalid': errorMessages && errorMessages.length }, 'form-control date-button position-absolute']"
      :disabled="disabled"
      :id="id"
      :name="name"
      :aria-label="getTranslation(label)"
      @input="emitDateValue(inputValue)"
      :min="min"
      :max="max"
      locale="de" />
  </div>
</template>

<script>
import { isEqual, cloneDeep } from 'lodash';
import {
  BFormDatepicker,
} from 'bootstrap-vue';
import TranslationMixin from '@forgerock/platform-shared/src/mixins/TranslationMixin';
import FrInputLayout from '../Wrapper/InputLayout';
import InputMixin from '../Wrapper/InputMixin';

/**
 * Input for date (YYYY-MM-DD) with a floating label in the center, this will move when a user types into the input.
 */
export default {
  name: 'DateInput',
  mixins: [
    InputMixin,
    TranslationMixin,
  ],
  components: {
    BFormDatepicker,
    FrInputLayout,
  },
  props: {
    dropleft: {
      default: true,
      type: Boolean,
    },
    inputAttributes: {
      /** 
       * CIAM-931 - this is a custom CIAM prop, meant to be used to pass additional attributes
       * to the input field. In this case it is assumed the object will contain two keys: `min` and `max`. 
       * 
       * The value of `min` determines how many years in the past the minimum value of the date
       * is allowed to be set.
       * 
       * The value of `max` determines how many years in the future the maximum value of the date
       * is allowed to be set.
       */
      type: Object,
      default: {},
    },
  },
  computed: {
    now() {
      const now = new Date();
      now.setHours(0,0,0,0);
      return now;
    },
    min() {
      if (!this.inputAttributes || (!this.inputAttributes.min && this.inputAttributes.min !== 0)) {
        return null;
      }
      const minDate = new Date(this.now);
      minDate.setFullYear(minDate.getFullYear() + (this.inputAttributes.min * -1));
      return minDate;
    },
    max() {
      if (!this.inputAttributes || (!this.inputAttributes.max && this.inputAttributes.max !== 0)) {
        return null;
      }
      const maxDate = new Date(this.now);
      maxDate.setFullYear(maxDate.getFullYear() + +this.inputAttributes.max);
      return maxDate;
    },
  },
  methods: {
    /**
     * CIAM-931 - CIAM onboarding journey expects a custom date format.
     * The buildFormattedDate method was split in two parts, to be reused
     * because of this.
     * 
     * @param {String} date value in YYYY-MM-DD format (when filling in the form for the first time)
     * or in DD.MM.YYYY format (when filling it in again after an error)
     * @returns {Object} custom object with the string values of year, month and date
     */
    splitDateValue(dateValue) {
      if (dateValue.includes('.')) {
        try {
          this.emitDateValue(dateValue);
          const day = dateValue.slice(0,2);
          const month = dateValue.slice(3,5);
          const year = dateValue.slice(6,10);
          return {day, month, year}
        } catch (error) {
          return null;
        }
      } else {
        try {
          const date = new Date(dateValue);
          const day = (`0${date.getUTCDate()}`).slice(-2);
          const month = (`0${date.getUTCMonth() + 1}`).slice(-2);
          const year = (`000${date.getUTCFullYear()}`).slice(-4);
          if (isNaN(day) || isNaN(month) || isNaN(year)) {
            return null;  // all vars need to be numeric
          }
          return { day, month, year };
        } catch (error) {
          return null;
        }
      }
    },
    /**
     * Constructs a formatted and validated date in the form of YYYY-MM-DD
     *
     * @param {String} dateValue Currently entered date value
     * @returns {String} The fully formatted date string
     */
    buildFormattedDate(dateValue) {
      const dateParts = this.splitDateValue(dateValue);
      return dateParts ? `${dateParts.year}-${dateParts.month}-${dateParts.day}` : '';
    },
    /**
     * Sets the date value for both the date picker and the date input
     *
     * @param {String} inputValue value to be set for input value
     */
    inputValueHandler(inputValue) {
      if (inputValue) {
        this.inputValue = this.buildFormattedDate(inputValue);
      }
    },
    /**
     * first checks if the date is already formatted and validated (contains "."), then
     * emits out formatted and validated date value.
     *
     * @param {String} newVal value to be emitted
     * @emits {String} input - The current date value entered
     */
    emitDateValue(newVal) {
      if (newVal.includes('.')) {
        this.$emit('input', newVal);
      } else {
        if (!this.validateDateRange(newVal)) {
          return;
        }

        /**
         * CIAM-931 - CIAM onboarding journey expects a custom date format.
         * This method is where the form state gets updated. So internally
         * the DateInput operates on YYYY-MM-DD date format, but the form
         * will be submitted with a DD.MM.YYYY format.
         */

        const dateParts = this.splitDateValue(newVal);
        let emitVal = dateParts ? `${dateParts.day}.${dateParts.month}.${dateParts.year}` : '';
        this.$emit('input', emitVal);
      }
    },
    /**
    * formats input value to be in the format that date picker and input expect
    *
    * @param {Number|String} newVal value to be set for input value
    */
    setInputValue(newVal) {
      if (newVal !== undefined && newVal !== null && newVal !== '' && !isEqual(this.oldValue, newVal)) {
        this.inputValue = this.buildFormattedDate(newVal);
        this.oldValue = cloneDeep(newVal);
      }
    },
    /**
     * CIAM-931 custom validation method. Min and max range of dates can be defined in
     * the `inputAttributes` prop (see above).
     * This method validates if the chosen date fits in that range and shows errors
     * if not.
     * 
     * @param {String} newVal value to be set/emitted
     * @returns {Boolean} a flag indicate if the date is valid
     */
    validateDateRange(newVal) {
      if (!newVal) {
        return true;
      }

      const newDate = new Date(newVal);
      newDate.setHours(0,0,0,0);
      const newDateTime = newDate.getTime();
      if (newDateTime > this.max.getTime() || newDateTime < this.min.getTime()) {
        // TODO: the error message could be extracted to `stage` config later on
        this.$emit('ergo-id-validation-error', { name: this.name, message: "Bitte überprüfen Sie Ihre Eingabe" });
        return false;
      }

      this.$emit('ergo-id-validation-clear', this.name);
      return true;
    }
  },
};
</script>

<style lang="scss" scoped>
  .date-button {
    width: 46.5px;
    border: none;
    right: 1px;
    top: 1px;
    height: auto; // CIAM-931 fix for error label styling

    ::v-deep button {
      background-color: initial;
      color: $gray-500;
      padding: 0.85rem 0.5rem;
    }

    ::v-deep &.show > .btn-secondary.dropdown-toggle {
      background-color: initial;
      color: $gray-500;
    }
  }

  .form-control.is-invalid {
    background-image: none;
  }
</style>
